import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs/internal/Observable';
import { NotificationDetails } from '../models/notificationDetails';
import { PaginationResult } from '../models/pagination-result';
import { HttpParams } from '@angular/common/http';
import { EmployeeMini } from '../models/employee-mini';
import { JuniorPromotionDto } from '../models/junior-promotion';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
    API_URL = `${environment.apiUrl}/notification`;
  
  constructor(private http: HttpClient) { }

  getNotifications(email: string): Observable<NotificationDetails[]> {
    return this.http.get<NotificationDetails[]>(`${this.API_URL}/unread?email=${email}`);
  }

  getLastNotifications(email?: string): Observable<NotificationDetails[]> {
    const query = email ? `?email=${encodeURIComponent(email)}` : '';
    return this.http.get<NotificationDetails[]>(`${this.API_URL}/last-notifications${query}`);
  }

  getAllNotifications(email?: string): Observable<NotificationDetails[]> {
    const query = email ? `?email=${encodeURIComponent(email)}` : '';
    return this.http.get<NotificationDetails[]>(`${this.API_URL}${query}`);
  }

  getMyNotifications(page = 0, size = 20): Observable<PaginationResult<NotificationDetails[]>> {
    const params = new HttpParams()
      .set('page', page)
      .set('size', size);

    return this.http.get<PaginationResult<NotificationDetails[]>>(`${this.API_URL}/my`, { params });
  }

  markNotificationAsRead(notificationId: string): Observable<void> {
    return this.http.put<void>(`${this.API_URL}/${notificationId}/read`, null);
  }

  triggerCddNotification(): Observable<void> {
    return this.http.get<void>(`${environment.apiUrl}/email/notify-contract-ending`);
  }

  triggerAnapecNotification(): Observable<void> {
    return this.http.get<void>(`${environment.apiUrl}/email/notify-anapec-contract-type`);
  }

  getContractEndingEmployees(): Observable<EmployeeMini[]> {
    return this.http.get<EmployeeMini[]>(`${environment.apiUrl}/employees/contract-ending`);
  }

  getTrialPeriodEndingEmployees(): Observable<EmployeeMini[]> {
    return this.http.get<EmployeeMini[]>(`${environment.apiUrl}/employees/trial-period-ending`);
  }

  getEmployeeSupervisors(employeeId: number): Observable<EmployeeMini[]> {
    return this.http.get<EmployeeMini[]>(`${environment.apiUrl}/employees/${employeeId}/supervisors`);
  }

  getPotentialContractEndingRecipients(employeeId: number): Observable<EmployeeMini[]> {
    return this.http.get<EmployeeMini[]>(`${environment.apiUrl}/employees/${employeeId}/contract-ending-recipients`);
  }

  getTrialPeriodEndingRecipients(employeeId: number): Observable<EmployeeMini[]> {
    return this.http.get<EmployeeMini[]>(`${environment.apiUrl}/employees/${employeeId}/trial-period-recipients`);
  }

  sendContractEndingEmail(): Observable<void> {
    return this.http.get<void>(`${environment.apiUrl}/email/send-contract-ending-email`);
  }

  sendContractEndingEmailForEmployee(employeeId: number): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/email/send-contract-ending-email/${employeeId}`, null);
  }

  sendContractEndingReminder(): Observable<void> {
    return this.http.get<void>(`${environment.apiUrl}/email/send-contract-ending-reminder`);
  }

  triggerTrialPeriodNotification(): Observable<void> {
    return this.http.get<void>(`${environment.apiUrl}/email/notify-trial-period-ending`);
  }

  sendTrialPeriodEndingEmail(): Observable<void> {
    return this.http.get<void>(`${environment.apiUrl}/email/send-trial-period-ending-email`);
  }

  sendTrialPeriodEndingEmailForEmployee(employeeId: number): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/email/send-trial-period-ending-email/${employeeId}`, null);
  }

  sendTrialPeriodEndingReminder(): Observable<void> {
    return this.http.get<void>(`${environment.apiUrl}/email/send-trial-period-ending-reminder`);
  }

  getJuniorPromotionCandidates(): Observable<JuniorPromotionDto[]> {
    return this.http.get<JuniorPromotionDto[]>(`${environment.apiUrl}/employees/junior-promotion`);
  }

  searchManualJuniorPromotionEmployees(keyword: string): Observable<EmployeeMini[]> {
    const params = new HttpParams().set('keyword', keyword);
    return this.http.get<EmployeeMini[]>(`${environment.apiUrl}/employees/junior-promotion/search`, { params });
  }

  addManualJuniorPromotionCandidate(employeeId: number, comment?: string): Observable<void> {
    let params = new HttpParams();
    if (comment) params = params.set('comment', comment);
    return this.http.post<void>(`${environment.apiUrl}/employees/junior-promotion/manual-candidates/${employeeId}`, null, { params });
  }

  validateJuniorPromotion(employeeId: number, decision: string, comment?: string, postponeDate?: Date | null): Observable<void> {
    let params = new HttpParams().set('decision', decision);
    if (comment) {
      params = params.set('comment', comment);
    }
    // A postpone date is meaningful only for a POSTPONED decision. Keeping a
    // previously selected date on a VALIDATED request is misleading and can
    // make the backend try to parse an irrelevant value.
    if (decision === 'POSTPONED' && postponeDate) {
      params = params.set('postponeDate', postponeDate.toISOString());
    }
    return this.http.patch<void>(`${environment.apiUrl}/employees/junior-promotion/${employeeId}/validate`, null, { params });
  }

  undoJuniorPromotionSupervisorDecision(employeeId: number): Observable<void> {
    return this.http.patch<void>(`${environment.apiUrl}/employees/junior-promotion/${employeeId}/undo-supervisor-decision`, null);
  }

  requestDoubleJuniorPromotion(employeeId: number): Observable<void> {
    return this.http.patch<void>(`${environment.apiUrl}/employees/junior-promotion/${employeeId}/double-promotion`, null);
  }

  undoDoubleJuniorPromotion(employeeId: number): Observable<void> {
    return this.http.patch<void>(`${environment.apiUrl}/employees/junior-promotion/${employeeId}/undo-double-promotion`, null);
  }

  bsaValidateJuniorPromotion(employeeId: number, decision: string, comment?: string): Observable<void> {
    let params = new HttpParams().set('decision', decision);
    if (comment) {
      params = params.set('comment', comment);
    }
    return this.http.patch<void>(`${environment.apiUrl}/employees/junior-promotion/${employeeId}/bsa-validate`, null, { params });
  }

  sendJuniorPromotionEmail(): Observable<void> {
    return this.http.get<void>(`${environment.apiUrl}/email/send-junior-promotion-email`);
  }

  sendJuniorPromotionEmailForEmployee(employeeId: number): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/email/send-junior-promotion-email/${employeeId}`, null);
  }
}
